import { multipleProperties } from './config';
import {
    ENCODING,
} from '../constants-vcard4';
import { decode as decodeQuotedPrintable } from 'quoted-printable';
import { TextDecoder } from 'text-encoding';

// Note: Using quoted-printable package for decoding and text-encoding for charset conversion
// This properly handles VCards with different character encodings (UTF-8, ISO-8859-1, Windows-1252, etc.)

function parseVCardTime(timeStr) {
    let hours, minutes, seconds;

    if (timeStr.length >= 6) {
        // "HHMMSS" or "--MMSS" format
        hours = parseInt(timeStr.substr(0, 2), 10);
        minutes = parseInt(timeStr.substr(2, 2), 10);
        seconds = parseInt(timeStr.substr(4, 2), 10);
    } else if (timeStr.length >= 4) {
        // "---SS" format
        hours = 0;
        minutes = 0;
        seconds = parseInt(timeStr.substr(2), 10);
    } else {
        throw new Error("Invalid time format");
    }

    return new Date(1970, 0, 1, hours, minutes, seconds, 0);
}

function parseVCardUTCOffset(offsetStr) {

    let regex = /^([+-])(\d{2})(\d{2})?$/;
    let matches = offsetStr.match(regex);

    if (!matches) {
        throw new Error("Invalid UTC offset format");
    }

    let factor = matches[1] === '+' ? 1 : -1; // For the direction
    let hours = parseInt(matches[2], 10);
    let minutes = matches[3] ? parseInt(matches[3], 10) : 0;

    return factor * (hours * 60 + minutes);
}

export function parseVCardDate(dateStr) {
    // vCard dates can be in YYYYMMDD or --MMDD or ---DD format
    let year, month, day;
    if (dateStr.length === 8) {
        year = parseInt(dateStr.substr(0, 4), 10);
        month = parseInt(dateStr.substr(4, 2), 10) - 1; // Months are 0-based in JavaScript Date object
        day = parseInt(dateStr.substr(6, 2), 10);
    } else if (dateStr.length === 6) {
        year = undefined;
        month = parseInt(dateStr.substr(2, 2), 10) - 1; // Months are 0-based in JavaScript Date object
        day = parseInt(dateStr.substr(4, 2), 10);
    } else if (dateStr.length === 5) {
        year = undefined;
        month = undefined;
        day = parseInt(dateStr.substr(3, 2), 10);
    } else {
        throw new Error("Invalid date format");
    }
    return new Date(year || 1970, month || 0, day || 1);
}

export function assignPropertyValue(obj, key, cardEntry) {
    // Make a shallow copy of obj
    const newObj = {...obj};
    // As array spread (...) does not deep copy array, if elements of array are objects, you might need to do a deep copy here depending on your use-case.

    if(multipleProperties.includes(key)) {
        if(Array.isArray(newObj[key])) {
            newObj[key] = [...newObj[key], cardEntry];
        } else if(newObj[key] !== undefined) {
            newObj[key] = [newObj[key], cardEntry];
        } else {
            newObj[key] = [cardEntry];
        }
    } else {
        newObj[key] = cardEntry;
    }
    return newObj;
}

export function decodingValueWithMetadata(value, metadata) {
    if(metadata.encoding && metadata.encoding.toLowerCase() === ENCODING.QUOTED_PRINTABLE) {
        // First decode the quoted-printable encoding
        const decodedValue = decodeQuotedPrintable(value);

        // Then convert from the specified charset to UTF-8 if needed
        if(metadata.charset) {
            const charset = metadata.charset.toUpperCase();
            // Only convert if it's not already UTF-8
            if(charset !== 'UTF-8' && charset !== 'UTF8') {
                try {
                    // The decoded value from quoted-printable is a string where each
                    // character code represents a byte value
                    const bytes = new Uint8Array(decodedValue.length);
                    for(let i = 0; i < decodedValue.length; i++) {
                        bytes[i] = decodedValue.charCodeAt(i) & 0xFF;
                    }
                    // Use TextDecoder to interpret bytes according to the specified charset
                    const decoder = new TextDecoder(charset);
                    value = decoder.decode(bytes);
                } catch(e) {
                    console.warn(`Failed to decode from charset ${charset}, using as-is:`, e);
                    value = decodedValue;
                }
            } else {
                value = decodedValue;
            }
        } else {
            // No charset specified, assume UTF-8
            value = decodedValue;
        }
    }

    if (metadata.value) {
        switch (metadata.value.toLowerCase()) {
            case ENCODING.INTEGER:
                value = parseInt(value);
                break;
            case ENCODING.FLOAT:
                value = parseFloat(value);
                break;
            case ENCODING.BOOLEAN:
                value = (value.toLowerCase() === 'true');
                break;
            case ENCODING.DATE_TIME:
            case ENCODING.TIMESTAMP:
                value = new Date(value);
                break;
            case ENCODING.DATE:
                value = parseVCardDate(value);
                break;
            case ENCODING.TIME:
                value = parseVCardTime(value);
                break;
            case ENCODING.UTC_OFFSET:
                value = parseVCardUTCOffset(value);
                break;
            default: // Keep as text
                break;
        }
    }

    // Check if parsing operations resulted to valid values
    if (isNaN(value) && (metadata.value === ENCODING.INTEGER || metadata.value === ENCODING.FLOAT ||
        metadata.value === ENCODING.DATE_TIME || metadata.value === ENCODING.TIMESTAMP)) {
        throw new Error(`Invalid ${metadata.value} value`);
    }

    return value;
}